/**
 * "Enviar feedback" — o processo leve do beta (rodada 3, B7).
 *
 * Uma folha só: tipo · gravidade · tela (preenchida com a rota atual, e a
 * pessoa pode corrigir) · descrição · print opcional. O aparelho vai junto,
 * automático, sem dado da viagem. Sem rede, o rascunho fica guardado e a
 * folha diz isso em vez de falhar. O status vive no GitHub — o app não tem
 * administrador (Conta ≠ Viagem), então não há "meus feedbacks" aqui.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { reporter } from '../../observability/instalar';
import { useTheme } from '../../theme/ThemeProvider';
import { Icon } from '../../ui/icons';
import { Sheet } from '../../ui/Sheet';
import { enviarVariante } from '../memorias/api';
import { fotoReduzida } from '../memorias/imagem';
import { registrarAreaAberta } from '../../telemetry/useTelemetria';
import { feedbackApi, GRAVIDADES, guardarRascunho, lerAparelho, lerRascunho, TIPOS, type GravidadeDeFeedback, type TipoDeFeedback } from './api';

export function FeedbackSheet({ tripId, onFechar }: { tripId: string | null; onFechar: () => void }) {
  const { pathname } = useLocation();
  const { name: tema } = useTheme();
  const rascunho = useRef(lerRascunho()).current;
  const [tipo, setTipo] = useState<TipoDeFeedback>(rascunho?.type ?? 'bug');
  const [gravidade, setGravidade] = useState<GravidadeDeFeedback>(rascunho?.severity ?? 'atrapalha');
  const [tela, setTela] = useState(rascunho?.screen ?? pathname);
  const [descricao, setDescricao] = useState(rascunho?.description ?? '');
  const [print, setPrint] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  // Abrir a folha é "usar o feedback" (telemetria 2B2); enviar fica na tabela.
  useEffect(() => registrarAreaAberta('feedback', tripId ?? undefined), [tripId]);
  const [erro, setErro] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<{ issueUrl: string | null } | null>(null);
  const [semRede, setSemRede] = useState(!navigator.onLine);
  // Incidente recente (rodada 2B1): só a REFERÊNCIA vai junto — o detalhe
  // técnico fica first-party. A pessoa pode desmarcar.
  const diagnostico = useRef(reporter().ultimoRecente()).current;
  const [anexarDiagnostico, setAnexarDiagnostico] = useState(!!diagnostico);
  const arquivo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const on = () => setSemRede(false);
    const off = () => setSemRede(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const pode = descricao.trim().length >= 3 && !enviando;

  async function enviar() {
    if (!pode) return;
    const corpo = { type: tipo, severity: gravidade, screen: tela.trim() || pathname, description: descricao.trim() };
    if (!navigator.onLine) {
      guardarRascunho(corpo);
      setSemRede(true);
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      let blob: Blob | null = null;
      if (print) blob = await fotoReduzida(print, 1600, 0.8);
      const r = await feedbackApi.enviar({ ...corpo, tripId, device: lerAparelho(tema), screenshot: blob ? { mimeType: 'image/jpeg', bytes: blob.size } : null, diagnosticCode: anexarDiagnostico && diagnostico ? diagnostico.diagnosticCode : null });
      if (blob && r.upload) {
        try {
          await enviarVariante(r.upload, blob);
          await feedbackApi.printPronto(r.id);
        } catch {
          // O feedback já está registrado; o print é o extra.
        }
      }
      guardarRascunho(null);
      setEnviado({ issueUrl: r.issueUrl });
    } catch (e) {
      // Sem rede no meio do caminho: guarda para depois, não perde o texto.
      if (!navigator.onLine) {
        guardarRascunho(corpo);
        setSemRede(true);
      } else setErro(e instanceof Error ? e.message : 'Não foi possível enviar agora.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Sheet titleId="feedback-titulo" onFechar={onFechar} className="fb" testId="feedback-sheet">
        <h2 className="sheet__title" id="feedback-titulo">Enviar feedback</h2>
        {enviado ? (
          <>
            <p className="sheet__text" role="status" data-testid="feedback-enviado">
              Recebido — obrigado. {enviado.issueUrl ? 'Entrou na lista de acompanhamento do beta.' : 'Ficou registrado.'}
            </p>
            <button type="button" className="nv-btn nv-btn--primary nv-btn--block" onClick={onFechar}>
              Fechar
            </button>
          </>
        ) : (
          <>
            <p className="sheet__text">Bug, coisa confusa, melhoria ou ideia — tudo vale. Vai junto o modelo do aparelho e a tela, nada da viagem.</p>

            <div className="chip-row" role="radiogroup" aria-label="Tipo">
              {TIPOS.map((t) => (
                <button type="button" key={t.valor} role="radio" aria-checked={tipo === t.valor} className={tipo === t.valor ? 'pick pick--on' : 'pick'} onClick={() => setTipo(t.valor)} data-testid={`fb-tipo-${t.valor}`}>
                  {t.rotulo}
                </button>
              ))}
            </div>

            <div className="fin__segmented" role="radiogroup" aria-label="Gravidade">
              {GRAVIDADES.map((g) => (
                <button type="button" key={g.valor} role="radio" aria-checked={gravidade === g.valor} className={gravidade === g.valor ? 'fin__seg fin__seg--ativo' : 'fin__seg'} onClick={() => setGravidade(g.valor)} title={g.dica} data-testid={`fb-sev-${g.valor}`}>
                  {g.rotulo}
                </button>
              ))}
            </div>
            <p className="auth-note fb__dica">{GRAVIDADES.find((g) => g.valor === gravidade)?.dica}</p>

            <label className="field">
              <span className="field__label">Tela</span>
              <input className="field__input" value={tela} onChange={(e) => setTela(e.target.value)} data-testid="fb-tela" />
            </label>
            <label className="field">
              <span className="field__label">O que aconteceu</span>
              <textarea className="field__input fb__texto" rows={4} value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="O que você esperava, o que aconteceu, e como repetir." data-testid="fb-descricao" />
            </label>

            {diagnostico && (
              <label className="fb__diagnostico">
                <input type="checkbox" checked={anexarDiagnostico} onChange={(e) => setAnexarDiagnostico(e.target.checked)} data-testid="fb-diagnostico" />
                <span>
                  Anexar o diagnóstico <strong>{diagnostico.diagnosticCode}</strong> (só o código — sem dados seus)
                </span>
              </label>
            )}

            <input ref={arquivo} type="file" accept="image/*" hidden onChange={(e) => setPrint(e.target.files?.[0] ?? null)} data-testid="fb-print" />
            <div className="fb__print">
              <button type="button" className="nv-btn nv-btn--chip" onClick={() => arquivo.current?.click()}>
                <Icon name="memorias" size={16} /> {print ? 'Trocar print' : 'Anexar print'}
              </button>
              {print && (
                <span className="fb__print-nome">
                  {print.name}
                  <button type="button" className="fb__print-tirar" onClick={() => setPrint(null)} aria-label="Tirar o print">
                    <Icon name="fechar" size={14} />
                  </button>
                </span>
              )}
            </div>

            {semRede && (
              <p className="auth-note" role="status" data-testid="fb-sem-rede">
                Sem conexão — o texto fica guardado neste aparelho. Abra de novo quando a rede voltar e toque em Enviar.
              </p>
            )}
            {erro && (
              <p className="auth-error" role="alert">
                {erro}
              </p>
            )}
            <div className="fin__ctas">
              <button type="button" className="nv-btn nv-btn--secondary" onClick={onFechar}>
                Cancelar
              </button>
              <button type="button" className="nv-btn nv-btn--primary" disabled={!pode} onClick={() => void enviar()} data-testid="fb-enviar">
                {enviando ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </>
        )}
    </Sheet>
  );
}
